import { useState } from 'react';
import { generateLiteratureReview } from '../api';

function Spinner() {
  return (
    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg className="w-10 h-10 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
    </svg>
  );
}

const SECTION_COLORS = {
  'Introduction': 'border-indigo-200 bg-indigo-50/40',
  'Thematic Analysis': 'border-violet-200 bg-violet-50/40',
  'Methodological Overview': 'border-teal-200 bg-teal-50/40',
  'Key Findings and Consensus': 'border-green-200 bg-green-50/40',
  'Research Gaps and Future Directions': 'border-amber-200 bg-amber-50/40',
  'Conclusion': 'border-blue-200 bg-blue-50/40',
};

const SECTION_HEADER_COLORS = {
  'Introduction': 'text-indigo-700',
  'Thematic Analysis': 'text-violet-700',
  'Methodological Overview': 'text-teal-700',
  'Key Findings and Consensus': 'text-green-700',
  'Research Gaps and Future Directions': 'text-amber-700',
  'Conclusion': 'text-blue-700',
};

export default function LiteratureReview({ papers = [], isPro = true }) {
  const [topic, setTopic] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [editingSections, setEditingSections] = useState({});

  const canSubmit = topic.trim() && papers.length >= 2 && !loading && isPro;

  const handleGenerate = async () => {
    setLoading(true);
    setError('');
    setResult(null);
    setEditingSections({});
    try {
      const res = await generateLiteratureReview(topic.trim(), papers);
      setResult(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map((d) => d.msg).join(', ')
        : typeof detail === 'string' ? detail : 'Literature review generation failed.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSectionEdit = (index, newContent) => {
    setResult((prev) => {
      const sections = [...prev.sections];
      sections[index] = { ...sections[index], content: newContent };
      return { ...prev, sections };
    });
  };

  const toggleEdit = (index) => {
    setEditingSections((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  const handleCopy = async () => {
    if (!result) return;
    const text = [
      `LITERATURE REVIEW: ${result.topic}\n`,
      ...result.sections.map((s) => `## ${s.title}\n\n${s.content}`),
    ].join('\n\n---\n\n');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isPro) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Literature Review Generator</h2>
          <p className="text-sm text-gray-500 mt-1">
            Generate a structured academic literature review from your workspace papers.
          </p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-8 shadow-sm flex flex-col items-center text-center gap-4">
          <LockIcon />
          <div>
            <p className="text-gray-800 font-semibold text-base">Pro Feature</p>
            <p className="text-gray-500 text-sm mt-1 max-w-sm">
              Literature review generation is available on Pro and above. Upgrade to unlock AI-powered synthesis from your literature workspace.
            </p>
          </div>
          <a
            href="/pricing"
            className="px-5 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 transition-colors"
          >
            Upgrade to Pro
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-800">Literature Review Generator</h2>
        <p className="text-sm text-gray-500 mt-1">
          AI synthesises your workspace papers into a structured academic literature review.
        </p>
      </div>

      {/* Input form */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4 shadow-sm">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Review Topic / Focus
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && canSubmit && handleGenerate()}
            placeholder="e.g. CRISPR base editing in haematopoietic stem cells"
            className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            disabled={loading}
          />
          <p className="text-xs text-gray-400 mt-1">
            Be specific — a focused topic produces a more analytical review.
          </p>
        </div>

        {papers.length < 2 && (
          <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            Add at least 2 papers to your workspace to generate a literature review.
          </p>
        )}

        <button
          onClick={handleGenerate}
          disabled={!canSubmit}
          className="w-full px-4 py-2 bg-indigo-600 text-white rounded-md font-medium text-sm
                     hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed
                     transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Spinner />
              Generating review — this may take 20–30 seconds...
            </>
          ) : (
            <>Generate Literature Review ({papers.length} paper{papers.length !== 1 ? 's' : ''})</>
          )}
        </button>

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {error}
          </p>
        )}
      </div>

      {/* Result */}
      {result && (
        <div className="space-y-4">
          {result.mock_mode && (
            <div className="bg-amber-50 border border-amber-200 rounded-md px-4 py-2 text-xs text-amber-700 font-medium">
              MOCK MODE — Add your OPENROUTER_API_KEY to backend/.env for real AI output
            </div>
          )}

          {/* Review header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-semibold text-gray-800">
                Literature Review:{' '}
                <span className="text-indigo-700">{result.topic}</span>
              </h3>
              <p className="text-xs text-gray-400 mt-0.5">
                {result.sections.length} sections · ~{result.word_count.toLocaleString()} words
              </p>
            </div>
            <button
              onClick={handleCopy}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                copied
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
              }`}
            >
              {copied ? 'Copied!' : 'Copy All'}
            </button>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            {result.sections.map((section, index) => {
              const borderColor = SECTION_COLORS[section.title] || 'border-gray-200 bg-white';
              const headerColor = SECTION_HEADER_COLORS[section.title] || 'text-gray-700';
              const isEditing = editingSections[index];

              return (
                <div
                  key={index}
                  className={`border rounded-lg overflow-hidden ${borderColor}`}
                >
                  <div className="flex items-center justify-between px-4 py-2.5 border-b border-inherit">
                    <h4 className={`text-sm font-semibold ${headerColor}`}>
                      {section.title}
                    </h4>
                    <button
                      onClick={() => toggleEdit(index)}
                      className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      {isEditing ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  <div className="px-4 py-3">
                    {isEditing ? (
                      <textarea
                        value={section.content}
                        onChange={(e) => handleSectionEdit(index, e.target.value)}
                        rows={8}
                        className="w-full text-sm text-gray-800 border border-gray-300 rounded px-3 py-2
                                   focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-y bg-white"
                      />
                    ) : (
                      <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                        {section.content}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Copy bottom */}
          <div className="flex justify-end pt-1">
            <button
              onClick={handleCopy}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
                copied
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
              }`}
            >
              {copied ? 'Copied!' : 'Copy Full Review'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
